/**
 * 학원 상세 안내(주소·운영시간·연락처·홈페이지).
 *
 * 지점 상세의 BranchInfoSection과 같은 모양이지만, 학원은 요일 라벨('월요일')·공휴일·
 * 운영시간 안내문·홈페이지가 추가로 필요하다. 지점 상세에 영향을 주지 않도록 따로 둔다
 * (운전연수 사이트는 BranchInfoSection 하나에 옵션으로 합쳐 두었다).
 */
import BranchAddress from '@/app/branch/[branchSlug]/components/branch-address';
import { AcademyHourType } from '@/services/types/academy.type';
import Image from 'next/image';
import Link from 'next/link';

interface Props {
  title?: string;
  address: string[];
  roadAddress: string;
  hours: AcademyHourType[];
  phoneNumber: string;
  operatingHoursNote?: string | null;
  operatingHoursAddNote?: string | null;
  homepage?: string | null;
}

// 시간 포맷팅: '10:00:00 ~ 17:00:00' → '10:00 ~ 17:00'
function formatHourRange(time: string) {
  if (!time) return '';
  const [start, end] = time.split('~').map((t) => t.trim());
  const format = (t: string) => (t ? t.slice(0, 5) : '');
  return `${format(start)} ~ ${format(end)}`;
}

export default function AcademyInfoSection({
  title = '학원안내',
  address,
  roadAddress,
  hours,
  phoneNumber,
  operatingHoursNote,
  operatingHoursAddNote,
  homepage,
}: Props) {
  const findTime = (label: string) =>
    hours.find((h) => h.label === label && h.time)?.time;
  const weekday = findTime('월요일');
  const weekend = findTime('토요일');
  const holiday = findTime('공휴일');
  const hasHours = !!(weekday || weekend || holiday);

  return (
    <section>
      <h3 className="desktop:pb-8 text-level-2 desktop:typo-24 typo-22 pb-5 font-extrabold">
        {title}
      </h3>
      <BranchAddress
        address={address}
        roadAddress={roadAddress}
        subways={[]}
        markers={[]}
      />

      {/* 운영시간 */}
      <div className="desktop:flex">
        <div className="desktop:flex desktop:pb-9 gap-4 pb-7">
          <div className="desktop:w-[100px] desktop:items-start desktop:pb-5 flex pb-4">
            <Image
              src="/images/icons/icon_clock.svg"
              alt="운영시간"
              width={20}
              height={20}
            />
            <span className="typo-20 text-level-3 desktop:pl-2 pl-1 font-bold">
              운영시간
            </span>
          </div>
          <div className="desktop:pl-6 flex flex-col gap-3">
            {[
              { label: '평일', time: weekday },
              { label: '주말', time: weekend },
              { label: '공휴일', time: holiday },
            ]
              .filter((row) => row.time)
              .map((row) => (
                <div
                  key={row.label}
                  className="text-level-3 flex items-center space-x-1 text-lg"
                >
                  <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
                    {row.label}
                  </span>
                  <span className="typo-16 text-level-4 desktop:typo-18 font-medium">
                    {formatHourRange(row.time as string)}
                  </span>
                </div>
              ))}
            {operatingHoursNote && (
              <div className="typo-16 text-level-3 desktop:typo-18 font-medium">
                <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
                  비고
                </span>{' '}
                {operatingHoursNote}
              </div>
            )}
            {hasHours ? (
              operatingHoursAddNote && (
                <div className="typo-16 text-level-6 leading-snug">
                  {operatingHoursAddNote}
                </div>
              )
            ) : (
              <span className="desktop:typo-18 text-level-4 font-bold">
                학원문의
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 연락처 */}
      {phoneNumber && (
        <div className="desktop:flex desktop:pb-9 gap-4 pb-7">
          <div className="desktop:w-[100px] desktop:items-start desktop:pb-0 flex pb-4">
            <Image
              src="/images/icons/icon_call.svg"
              alt="연락처"
              width={20}
              height={20}
            />
            <span className="typo-20 text-level-3 desktop:pl-2 pl-1 font-bold">
              연락처
            </span>
          </div>
          <div className="desktop:pl-6 flex flex-col gap-3">
            <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
              {phoneNumber}
            </span>
          </div>
        </div>
      )}

      {/* 홈페이지 */}
      {homepage && (
        <div className="desktop:flex desktop:pb-9 gap-4 pb-7">
          <div className="desktop:w-[100px] desktop:items-start desktop:pb-0 flex pb-4">
            <Image
              src="/images/icons/icon_homepage.svg"
              alt="홈페이지"
              width={20}
              height={20}
            />
            <span className="typo-20 text-level-3 desktop:pl-2 pl-1 font-bold">
              홈페이지
            </span>
          </div>
          <div className="desktop:pl-6 flex flex-col gap-3">
            <Link
              href={homepage}
              className="desktop:typo-18 typo-16 text-level-4 font-bold break-all"
              target="_blank"
            >
              {homepage}
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
